import { fromJS } from "immutable";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import AssignTo from "../board/components/AssignTo";
import ProgressField from "./ProgressField";
import DropdownField from "../board/components/DropdownField";
import CustomField from "../main/CustomField";
import LinkItemPickerV2, { renderItemIcon } from "../main/component/LinkItemPickerV2";
import InputInline from "../components/InputInline";
import { InformationCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import AmountField from "../components/AmountField";
import LabelsSelect from "../components/LabelsSelect";
import { useQuery } from "../AppLayout";
import { ActivityStatus } from "../activities/ActivityIndicator";
import React, { useState } from "react";
import Editor from "../components/Editor";
import ActionableField from "./ActionableField";
import { useNavigate } from "react-router-dom";
import { navigateToItem } from "../Navigator";
import SelectionField from "./SelectionField";
import { ContentTypes } from "../constants/action-types";
import ActivityAction from "../actions/ActivityAction";
import TaskAction from "../actions/TaskAction";
import HoverManager from "../components/HoverManager";
import LayoutAction from "../actions/LayoutAction";
import DefaultAvatar from "../components/DefaultAvatar";
import DomainsField from "../board/components/TaskDetail/DomainsField";
import PhoneInputField from "../board/components/TaskDetail/PhoneInputField";
import EmailInputField from "../board/components/TaskDetail/EmailInputField";
import WebsiteInputField from "../board/components/TaskDetail/WebsiteInputField";

type Props = {
  row: any;
  column: any;
  outlineNum?: string;
  workflowData?: any;
  editInline?: boolean;
  showAddRowFilter?: boolean;
  showSelectable?: boolean;
  onUpdateRow?: (row: any) => void;
  onChangeFilter?: (filter: any) => void;
};

export const renderStage = (stage) => {
  if (stage == "Lost") {
    return (
      <div className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
        {"Lost"}
      </div>
    );
  }
  if (stage == "Unqualified") {
    return (
      <div className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
        {"Unqualified"}
      </div>
    );
  }
  if (stage == "Won") {
    return (
      <div className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
        {"Won"}
      </div>
    );
  }
  return (
    <div className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ">
      New
    </div>
  );
};

export const renderConnectionStrength = (strengthValue) => {
  let connectionData = {
    styles: { backgroundColor: "#838282" },
    text: "No connection",
  };
  if (strengthValue > 10) {
    connectionData = {
      styles: { backgroundColor: "#008000" },
      text: "Very Strong",
    };
  } else if (strengthValue > 7) {
    connectionData = {
      styles: { backgroundColor: "#32CD32" },
      text: "Strong",
    };
  } else if (strengthValue > 4) {
    connectionData = {
      styles: { backgroundColor: "#FFD700" },
      text: "Moderate",
    };
  } else if (strengthValue > 2) {
    connectionData = {
      styles: { backgroundColor: "#FFA500" },
      text: "Weak",
    };
  } else if (strengthValue > 0) {
    connectionData = {
      styles: { backgroundColor: "#ff0059" },
      text: "Very weak",
    };
  }
  return (
    <div className="flex space-x-2 items-center px-2 py-1 text-xs font-medium text-gray-600 ">
      <span style={connectionData.styles} className="w-2 h-2 rounded-full"></span>
      <span>{connectionData.text}</span>
    </div>
  );
};

const CellRenderer = (props: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [websiteError, setWebsiteError] = useState(false);
  const {
    column,
    row,
    outlineNum = "",
    onUpdateRow = () => {},
    onChangeFilter,
    showAddRowFilter = false,
    showSelectable = false,
  } = props;
  const contentType = useQuery().get("type");
  const view = useQuery().get("view");
  const rowType = row.type || contentType;

  const buckets = useSelector((state: any) => state.bucketReducer.get(rowType)) || fromJS([]);
  const ruleReducer = useSelector((state: any) => state.ruleReducer);
  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));
  const field = fields.find((item: any) => item.get("id") == column.get("selector"));

  const getRuleFieldValues = (fieldId) => {
    return ruleReducer
      .get(fieldId)
      .toJS()
      .map((item) => ({ id: item.value, name: item.label }));
  };

  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const handleAddFilter = (newFilter) => {
    onChangeFilter({
      ...taskFilters.toJS(),
      ...newFilter,
    });
  };

  const isActivity = () => {
    return rowType == ContentTypes.ACTIVITY;
  };

  const getDataPath = () => {
    const selector = column.get("selector");
    if (isActivity()) {
      if (selector == "item") {
        return ["event", "data", column.get("selector"), "label"];
      }
      return ["event", "data", column.get("selector")];
    }
    return ["data", column.get("selector"), "value"];
  };

  const renderView = () => {
    const rowObject = fromJS(row);

    const changeUser = async (users: any) => {
      const newUsers = users.map((user: any) => user.accountId);
      handleUpdateItem("assignees", newUsers);
    };

    const handleUpdateItem = (field: string, value: any) => {
      if (!isActivity()) {
        dispatch(TaskAction.updateTask(rowObject.get("id"), field, value, contentType, view)).then(
          () => {
            onUpdateRow(row);
          }
        );
      }
    };

    //CustomField
    if (column.get("isCustomField") == true) {
      if (field) {
        const customFieldValue = rowObject.getIn(["data", field.get("id"), "value"]);
        const fieldData = {
          ...field.toJS(),
          value: customFieldValue,
        };
        const customFieldId = column.get("id");
        return (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              handleAddFilter({
                [customFieldId]: {
                  operator: "is",
                  values: [customFieldValue],
                },
              });
            }}
          >
            <CustomField
              isGrid={true}
              field={fieldData}
              onChange={(valueChange: string | Date) => {
                handleUpdateItem(field.get("id"), valueChange);
              }}
            />
          </ActionableField>
        );
      }
    }

    let component = null;

    const currentVal = rowObject.getIn(getDataPath());
    const fieldId = column.get("selector");
    const showRowSelection = showSelectable && !isActivity();

    switch (fieldId) {
      case "id":
        component = (
          <div style={{ maxWidth: column.get("width") }} className={"w-full flex truncate ..."}>
            {rowObject.get("id")}
          </div>
        );
        break;
      case "name":
        component = currentVal;
        break;
      case "summary":
        const isHoverable =
          rowObject.get("type") === "COMPANY" || rowObject.get("type") === "CONTACT";

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            {isHoverable ? (
              <HoverManager
                itemId={rowObject.get("id")}
                itemType={rowObject.get("type")}
                label={currentVal}
              >
                <div className={"flex items-center truncate w-full relative"}>
                  {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                  {rowObject.get("iconUrl") ? (
                    <img
                      src={rowObject.get("iconUrl")}
                      alt="Account Logo"
                      className="w-5 h-5 rounded-lg object-cover ml-1.5"
                    />
                  ) : (
                    <DefaultAvatar
                      name={rowObject.get("name")}
                      type={rowObject.get("type")}
                      size="sm"
                      className="ml-1.5"
                    />
                  )}
                  <InputInline
                    readOnly={isActivity()}
                    onFinish={(newValue) => {
                      if (currentVal != newValue) {
                        handleUpdateItem(fieldId, newValue);
                      }
                    }}
                    value={currentVal}
                  />
                </div>
              </HoverManager>
            ) : (
              <div className={"flex items-center truncate w-full relative"}>
                {showRowSelection && <SelectionField itemId={rowObject.get("id")} />}
                {rowObject.get("iconUrl") ? (
                  <img
                    src={rowObject.get("iconUrl")}
                    alt="Account Logo"
                    className="w-5 h-5 rounded-lg object-cover ml-1.5"
                  />
                ) : (
                  <DefaultAvatar
                    name={rowObject.get("name")}
                    type={rowObject.get("type")}
                    size="sm"
                    className="ml-1.5"
                  />
                )}
                <InputInline
                  readOnly={isActivity()}
                  onFinish={(newValue) => {
                    if (currentVal != newValue) {
                      handleUpdateItem(fieldId, newValue);
                    }
                  }}
                  value={currentVal}
                  rightAction={
                    <InformationCircleIcon
                      className={
                        "w-4 hover:text-blue-600 icon-action cursor-pointer absolute right-2 top-2"
                      }
                      onClick={() => {
                        if (isActivity()) {
                          dispatch(
                            ActivityAction.showActivityDialog(
                              rowObject.get("itemId"),
                              rowObject.get("id")
                            )
                          );
                        } else {
                          navigateToItem({ item: rowObject, navigate });
                        }
                      }}
                    />
                  }
                />
              </div>
            )}
          </ActionableField>
        );
        break;
      case "description":
      case "privateDescription":
        return (
          <div style={{ maxWidth: column.get("width") }} className={"w-full"}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "8px 12px",
              }}
              className="!max-h-6 !overflow-hidden !whitespace-nowrap"
            >
              <div style={{ overflow: "hidden" }}>
                <Editor
                  viewMode={true}
                  editorContent={{
                    htmlContent: currentVal || "",
                    contents: {},
                  }}
                />
              </div>
            </div>
          </div>
        );
      case "done":
        return (
          <div className={"w-full flex justify-center"}>
            <ActivityStatus
              activity={rowObject}
              itemId={rowObject.get("itemId")}
              onClickMarkAsDone={() => {
                onUpdateRow(row);
              }}
            />
          </div>
        );
      case "domains":
        component = (
          <DomainsField handleUpdateField={handleUpdateItem} isGrid domains={currentVal} />
        );
        break;
      case "assignees":
        component = (
          <AssignTo
            disabled={false}
            sizeIcon={18}
            isLeftIcon={false}
            label={false}
            assignees={currentVal}
            onChange={changeUser}
          />
        );
        break;
      case "finish":
      case "birthday":
      case "start":
      case "markedAsDoneTime":
      case "updatedTime":
        const date = new Date(currentVal);
        if (isValid(date)) {
          const dateFormat = column.get("format") || "yyyy-MM-dd HH:mm";
          if (dateFormat == "formatDistanceToNow") {
            component = <div>{formatDistanceToNow(date)}</div>;
          } else {
            component = <div>{format(date, dateFormat)}</div>;
          }
        }
        break;
      case "estimatedProbability":
        component = <ProgressField value={currentVal} />;
        break;
      case "phone":
        component = (
          <PhoneInputField
            onChange={(value, hasError) => {
              setPhoneError(hasError);
              console.log("hasError", hasError);
            }}
            onFinish={(newValue, hasError) => {
              setPhoneError(hasError);
              if (currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            error={phoneError}
            value={currentVal}
            isGrid={true}
          />
        );
        break;
      case "email":
        component = (
          <EmailInputField
            onChange={(value, hasError) => {
              setEmailError(hasError);
            }}
            onFinish={(newValue, hasError) => {
              setEmailError(hasError);
              if (!hasError && currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            error={emailError}
            value={currentVal}
            isGrid={true}
          />
        );
        break;
      case "website":
        component = (
          <WebsiteInputField
            onChange={(value, hasError) => {
              setWebsiteError(hasError);
            }}
            onFinish={(newValue, hasError) => {
              setWebsiteError(hasError);
              if (!hasError && currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            error={websiteError}
            value={currentVal}
            isGrid={true}
          />
        );
        break;
      case "jobTitle":
      case "linkedIn":
      case "angleList":
      case "instagram":
      case "facebook":
      case "twitter":
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <div className={"w-full hover:text-blue-600"}>
              <InputInline
                onFinish={(newValue) => {
                  if (currentVal != newValue) {
                    handleUpdateItem(column.get("selector"), newValue);
                  }
                }}
                value={currentVal}
              />
            </div>
          </ActionableField>
        );
        break;
      case "estimatedRevenue":
      case "price":
      case "unitPrice":
        return (
          <div className={"w-full flex justify-center"} title={currentVal}>
            <AmountField amount={currentVal} />
          </div>
        );
      case "stage":
      case "status":
        const options = getRuleFieldValues(fieldId);
        const selectedOption = options.find((item: any) => item.id == currentVal) || {};
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (selectedOption.id) {
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <DropdownField
              value={currentVal}
              options={options}
              onChange={async (newValue: string) => {
                if (newValue !== currentVal) {
                  handleUpdateItem(fieldId, newValue);
                }
              }}
            />
          </ActionableField>
        );
        break;
      case "street":
      case "postcode":
      case "billingStreet":
      case "billingPostcode":
        component = (
          <InputInline
            onFinish={(newValue) => {
              if (currentVal != newValue) {
                handleUpdateItem(column.get("selector"), newValue);
              }
            }}
            value={currentVal}
          />
        );
        break;
      case "outlineNumber":
        component = <div>{outlineNum}</div>;
        break;
      case "salutation":
        component = (
          <DropdownField
            leftIcon={<UserIcon width={20} />}
            defaultValue={{ name: "" }}
            value={currentVal}
            options={getRuleFieldValues("salutation")}
            onChange={async (id: string) => {
              if (id !== currentVal) {
                handleUpdateItem("salutation", id);
              }
            }}
          />
        );
        break;
      case "bucketId":
        const selectedBucket = buckets.find((item) => item.get("id") == currentVal) || fromJS({});
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (selectedBucket.get("id")) {
                handleAddFilter({
                  bucketId: {
                    operator: "in",
                    values: [currentVal],
                  },
                });
              }
            }}
          >
            <DropdownField
              value={currentVal}
              options={buckets && buckets.toJS()}
              onChange={(id: string) => {
                if (id !== currentVal) {
                  handleUpdateItem("bucketId", id);
                }
              }}
            />
          </ActionableField>
        );
        break;
      case "account":
        const accountValue = currentVal?.get("value");
        const accountLabel = currentVal?.get("label");

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            {accountValue ? (
              <HoverManager itemId={accountValue} itemType="COMPANY" label={accountLabel}>
                <LinkItemPickerV2
                  onChange={(item) => {
                    handleUpdateItem(fieldId, item);
                  }}
                  maxWidth={field.get("width")}
                  type="COMPANY"
                  projectId={rowObject.get("projectId")}
                  items={currentVal}
                  showInfoIcon={false}
                />
              </HoverManager>
            ) : (
              <LinkItemPickerV2
                onChange={(item) => {
                  handleUpdateItem(fieldId, item);
                }}
                maxWidth={field.get("width")}
                type="COMPANY"
                projectId={rowObject.get("projectId")}
                items={currentVal}
                showInfoIcon={false}
              />
            )}
          </ActionableField>
        );
        break;
      case "contact":
        const contactValue = currentVal?.get("value");
        const contactLabel = currentVal?.get("label");

        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            {contactValue ? (
              <HoverManager itemId={contactValue} itemType="CONTACT" label={contactLabel}>
                <LinkItemPickerV2
                  onChange={(item) => {
                    handleUpdateItem(fieldId, item);
                  }}
                  maxWidth={field.get("width")}
                  type="CONTACT"
                  projectId={rowObject.get("projectId")}
                  items={currentVal}
                  showInfoIcon={false}
                />
              </HoverManager>
            ) : (
              <LinkItemPickerV2
                onChange={(item) => {
                  handleUpdateItem(fieldId, item);
                }}
                maxWidth={field.get("width")}
                type="CONTACT"
                projectId={rowObject.get("projectId")}
                items={currentVal}
                showInfoIcon={false}
              />
            )}
          </ActionableField>
        );
        break;
      case "sale":
      case "issue":
        let dataType = "TASK";
        if (fieldId == "sale") {
          dataType = "SALE";
        }
        component = (
          <ActionableField
            showAddFilter={
              showAddRowFilter &&
              rowObject.get("type") !== "COMPANY" &&
              rowObject.get("type") !== "CONTACT"
            }
            onClickAddFilter={() => {
              if (currentVal) {
                const value = currentVal.get("value");
                handleAddFilter({
                  [fieldId]: {
                    operator: "in",
                    values: [value],
                  },
                });
              }
            }}
          >
            <LinkItemPickerV2
              onChange={(item) => {
                handleUpdateItem(fieldId, item);
              }}
              maxWidth={field.get("width")}
              type={dataType}
              projectId={rowObject.get("projectId")}
              items={currentVal}
            />
          </ActionableField>
        );
        break;
      case "item":
        component = (
          <div
            className={"w-full flex space-x-1 items-center"}
            data-tooltip-id={"information-tooltip"}
            onMouseEnter={() => {
              if (rowType !== "COMPANY" && rowType !== "CONTACT") {
                dispatch(
                  LayoutAction.hoverDataItem({
                    value: rowObject.getIn(["event", "data", column.get("selector"), "value"]),
                    label: currentVal,
                    type: rowType,
                    isOpen: true,
                  })
                );
              }
            }}
          >
            {renderItemIcon({}, rowObject.getIn(["event", "data", column.get("selector"), "type"]))}
            {currentVal}
          </div>
        );
        break;
      // Interaction fields
      case "firstComment":
      case "lastComment":
      case "firstEmail":
      case "lastEmail":
      case "lastActivity":
      case "firstActivity":
      case "firstMeeting":
      case "lastMeeting":
      case "firstCall":
      case "lastCall":
      case "lastMeetingInPerson":
      case "firstMeetingInPerson":
      case "firstTask":
      case "lastTask":
      case "firstDeadline":
      case "lastDeadline":
        const startDate =
          rowObject.getIn(["data", column.get("selector"), "activity", "event", "data", "start"]) ||
          "";
        const startTime =
          rowObject.getIn([
            "data",
            column.get("selector"),
            "activity",
            "event",
            "data",
            "startTime",
          ]) || "";
        const firstMeetingDate = new Date(`${startDate} ${startTime}`);
        if (isValid(firstMeetingDate)) {
          component = <div className={""}>{format(firstMeetingDate, "yyyy-MM-dd HH:mm")}</div>;
        } else {
          component = <div className={"bg-gray-50 h-full w-full"}>No communication</div>;
        }
        break;
      case "connectionStrength":
        const strengthValue = rowObject.getIn(["data", column.get("selector"), "value"]) || 0;
        component = (
          <div className={"flex truncate justify-center items-center"}>
            {renderConnectionStrength(strengthValue)}
          </div>
        );
        break;

      // Company fields
      case "country":
      case "city":
      case "state":
        component = (
          <div className={"flex truncate justify-center items-center"}>
            {rowObject.getIn(["data", column.get("selector"), "value", "label"])}
          </div>
        );
        break;
      case "labels":
        component = (
          <LabelsSelect
            projectId={rowObject.get("projectId")}
            contentType={rowType}
            type="GRID"
            value={currentVal ? currentVal.toJS() : []}
            onChange={(labelIds: string[]) => handleUpdateItem("labels", labelIds)}
            placeholder="Select labels"
          />
        );
        break;
      default:
        component = (
          <div className={"flex justify-center text-center w-full bg-gray-50"}>{currentVal}</div>
        );
        break;
    }
    return <div className={"px-2 w-full"}>{component}</div>;
  };

  return renderView();
};

export default CellRenderer;
